// filepath: apps/web/src/features/admin/effectsMetrics/queries.ts
//
// FR-UI-ADM-9 — TanStack Query hooks for the effects operations panel.
//
// Cache keys include `tenantSlug` as the second segment (INV-1).
// Switching tenants triggers a refetch and does NOT leak the
// prior tenant's snapshot.
//
// Mutations carry `X-Idempotency-Key: crypto.randomUUID()` (INV-3).
// The operator token is sent via the `useOperatorToken` hook and
// the existing `operatorFetch` helper — the token value is NEVER
// rendered in the DOM or persisted (INV-5).
//
// Auto-refresh interval is 30s (AC-5a). Manual refresh via
// `refetch()` resets the timer.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useOperatorToken } from "@/operator/useOperatorToken";
import { apiFetch } from "@/api";
import type {
  CircuitSnapshotResponse,
  EffectsMetricsFetchError,
  MetricsResponse,
  SetBreakerPolicyRequest,
  SetBreakerPolicyResponse,
} from "./types";
import { classifyEffectsMetricsError } from "./errorMessage";

/**
 * Cache keys (INV-1). tenantSlug is the second segment — mirroring
 * the hmacKeys/adminKeys shape.
 */
export const effectsMetricsKeys = {
  metrics: (tenantSlug: string) =>
    ["admin", "effects-metrics", tenantSlug] as const,
  circuit: (tenantSlug: string) =>
    ["admin", "effects-circuit", tenantSlug] as const,
};

/**
 * operatorFetch — fetch wrapper that injects the operator
 * Authorization header. Returns a typed error envelope on non-2xx.
 *
 * ISS-377: routes through the new `apiFetch` client with the
 * operator-token auth strategy. The client sets
 * `credentials: "omit"` and `Authorization: Bearer <token>`; the
 * OIDC `access_token` is NEVER consulted on this path.
 */
async function operatorFetch(
  token: string | null,
  path: string,
  init: { method?: string; headers?: HeadersInit; body?: BodyInit | null } = {},
): Promise<Response> {
  if (!token) {
    throw {
      kind: "error_unauthorized" as const,
      status: 401,
      message: "operator token not set",
    } satisfies EffectsMetricsFetchError;
  }
  const r = await apiFetch(path, {
    auth: { kind: "operator-token", token },
    init,
  });
  return r;
}

/**
 * useMetrics — GET /admin/effects/metrics.
 * Auto-refresh every 30 seconds (AC-5a).
 */
export function useMetrics(
  tenantSlug: string,
): UseQueryResult<MetricsResponse, EffectsMetricsFetchError> {
  const { token } = useOperatorToken();
  return useQuery<MetricsResponse, EffectsMetricsFetchError>({
    queryKey: effectsMetricsKeys.metrics(tenantSlug),
    queryFn: async () => {
      const r = await operatorFetch(token, "/admin/effects/metrics");
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const kind = classifyEffectsMetricsError(r.status, body);
        throw {
          kind,
          status: r.status,
          message: r.statusText,
        } satisfies EffectsMetricsFetchError;
      }
      return (await r.json()) as MetricsResponse;
    },
    refetchInterval: 30_000,
    enabled: token !== null,
  });
}

/**
 * useCircuitSnapshot — GET /admin/effects/circuit-breaker.
 * Auto-refresh every 30 seconds (banner transition detection).
 */
export function useCircuitSnapshot(
  tenantSlug: string,
): UseQueryResult<CircuitSnapshotResponse, EffectsMetricsFetchError> {
  const { token } = useOperatorToken();
  return useQuery<CircuitSnapshotResponse, EffectsMetricsFetchError>({
    queryKey: effectsMetricsKeys.circuit(tenantSlug),
    queryFn: async () => {
      const r = await operatorFetch(token, "/admin/effects/circuit-breaker");
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const kind = classifyEffectsMetricsError(r.status, body);
        throw {
          kind,
          status: r.status,
          message: r.statusText,
        } satisfies EffectsMetricsFetchError;
      }
      return (await r.json()) as CircuitSnapshotResponse;
    },
    refetchInterval: 30_000,
    enabled: token !== null,
  });
}

/**
 * useSetBreakerPolicy — POST /admin/effects/circuit-breaker/policy.
 * INV-3: X-Idempotency-Key from crypto.randomUUID() (no Math.random).
 * On success, invalidates the circuit snapshot cache.
 */
export function useSetBreakerPolicy(tenantSlug: string) {
  const { token } = useOperatorToken();
  const qc = useQueryClient();
  return useMutation<
    SetBreakerPolicyResponse,
    EffectsMetricsFetchError,
    SetBreakerPolicyRequest
  >({
    mutationFn: async (body) => {
      const idempotencyKey = crypto.randomUUID();
      const r = await operatorFetch(
        token,
        "/admin/effects/circuit-breaker/policy",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Idempotency-Key": idempotencyKey,
          },
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        const kind = classifyEffectsMetricsError(r.status, errBody);
        throw {
          kind,
          status: r.status,
          message: r.statusText,
        } satisfies EffectsMetricsFetchError;
      }
      return (await r.json()) as SetBreakerPolicyResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: effectsMetricsKeys.circuit(tenantSlug),
      });
    },
  });
}
